import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sun, Lock, IndianRupee, Receipt } from "lucide-react";
import api, { inr } from "../lib/api";

export default function PosDay() {
  const [day, setDay] = useState(null);
  const [openingFloat, setOpeningFloat] = useState("");
  const [counted, setCounted] = useState("");

  const load = () => api.get("/pos/day").then((r) => setDay(r.data));
  useEffect(() => { load(); }, []);

  const openDay = async () => {
    await api.post("/pos/day/open", { opening_float: parseInt(openingFloat || 0, 10) });
    toast.success("Day opened");
    setOpeningFloat("");
    load();
  };

  const closeDay = async () => {
    if (counted === "") return toast.error("Enter the counted cash");
    const { data } = await api.post("/pos/day/close", { counted_cash: parseInt(counted, 10) });
    toast.success(data.variance === 0 ? "Day closed — cash tallies!" : `Day closed — variance ${inr(data.variance)}`);
    setCounted("");
    load();
  };

  if (!day) return <div className="p-8 text-slate-500">Loading…</div>;

  const box = "bg-slate-900 border border-slate-800 rounded-lg p-5";

  return (
    <div className="p-8 max-w-4xl" data-testid="pos-day-page">
      <div className="flex items-center gap-2 mb-1">
        <Sun className="text-amber-500" size={22} />
        <h1 className="text-2xl font-semibold text-slate-100">Day Close & Cash Report</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6 font-mono">{day.day}</p>

      {/* Not opened yet */}
      {!day.open && !day.closed && (
        <div className={`${box} max-w-md`} data-testid="day-open-card">
          <h3 className="text-slate-200 mb-3">Open the day</h3>
          <p className="text-xs text-slate-500 mb-4">Count the cash in your drawer to start (opening float).</p>
          <div className="flex gap-2">
            <input type="number" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} placeholder="Opening float ₹" className="flex-1 bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono" data-testid="day-opening-float" />
            <button onClick={openDay} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-semibold text-sm" data-testid="day-open-btn">Open day</button>
          </div>
        </div>
      )}

      {/* Live summary (open or closed) */}
      {(day.open || day.closed) && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className={box}><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><IndianRupee size={13} /> Opening float</div><p className="text-2xl font-semibold text-slate-100 mt-2 font-mono">{inr(day.opening_float)}</p></div>
            <div className={box}><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Receipt size={13} /> Bills today</div><p className="text-2xl font-semibold text-slate-100 mt-2 font-mono">{day.bills}</p></div>
            <div className={box}><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">Sales today</div><p className="text-2xl font-semibold text-slate-100 mt-2 font-mono">{inr(day.sales_total)}</p></div>
            <div className={box}><div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">Expected cash</div><p className="text-2xl font-semibold text-amber-400 mt-2 font-mono">{inr(day.expected_cash)}</p></div>
          </div>

          <div className={`${box} mb-6`}>
            <h3 className="text-sm text-slate-300 uppercase tracking-widest mb-4">Payment collected today</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(day.payment_split).map(([m, v]) => (
                <div key={m}><p className="text-xs text-slate-500 uppercase">{m}</p><p className="text-lg text-slate-200 font-mono">{inr(v)}</p></div>
              ))}
            </div>
          </div>

          {/* Close form */}
          {day.open && (
            <div className={`${box} max-w-md`} data-testid="day-close-card">
              <h3 className="text-slate-200 mb-3 flex items-center gap-2"><Lock size={15} /> Close the day</h3>
              <p className="text-xs text-slate-500 mb-4">Count the cash in the drawer now. Expected: <span className="text-amber-400 font-mono">{inr(day.expected_cash)}</span></p>
              <div className="flex gap-2">
                <input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="Counted cash ₹" className="flex-1 bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono" data-testid="day-counted-cash" />
                <button onClick={closeDay} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-semibold text-sm" data-testid="day-close-btn">Close day</button>
              </div>
            </div>
          )}

          {/* Closed result */}
          {day.closed && (
            <div className={`${box} max-w-md`} data-testid="day-closed-card">
              <h3 className="text-slate-200 mb-3 flex items-center gap-2"><Lock size={15} /> Day closed</h3>
              <div className="space-y-2 font-mono text-sm">
                <div className="flex justify-between text-slate-400"><span>Expected cash</span><span>{inr(day.expected_cash)}</span></div>
                <div className="flex justify-between text-slate-400"><span>Counted cash</span><span>{inr(day.counted_cash)}</span></div>
                <div className={`flex justify-between font-bold pt-2 border-t border-slate-800 ${day.variance === 0 ? "text-emerald-400" : "text-red-400"}`}>
                  <span>{day.variance === 0 ? "Tallies ✓" : day.variance > 0 ? "Cash over" : "Cash short"}</span>
                  <span>{inr(Math.abs(day.variance))}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
